import { useCallback } from 'react';

import { 
    isGTMAvailable, 
    sendGTMEvent, 
    trackAPICall, 
    trackError,
    trackFileUpload,
    trackFormSubmission,
    trackPageView,
    trackPerformance,
    trackUserInteraction 
} from '../utils/gtm';
import { 
    ANALYTICS_CONFIG, 
    isAnalyticsEnabled, 
    isFeatureEnabled 
} from '../config/analytics';

export const useGTM = () => {

    // Track user interactions
    const trackClick = useCallback((
        element: string, 
        category: string = ANALYTICS_CONFIG.CATEGORIES.NAVIGATION,
        label?: string,
        value?: number
    ) => {
        if (isFeatureEnabled('TRACK_USER_INTERACTIONS')) {
            trackUserInteraction(
                ANALYTICS_CONFIG.ACTIONS.CLICK,
                category,
                label || element,
                value
            );
        }
    }, []);

    // Track form submissions
    const trackForm = useCallback((
        formName: string, 
        formData?: Record<string, any>
    ) => {
        if (isFeatureEnabled('TRACK_FORM_SUBMISSIONS')) {
            trackFormSubmission(formName, formData);
        }
    }, []);

    // Track file uploads
    const trackFile = useCallback((
        fileType: string, 
        fileSize: number, 
        success: boolean
    ) => {
        if (isFeatureEnabled('TRACK_FILE_UPLOADS')) {
            trackFileUpload(fileType, fileSize, success);
        }
    }, []);

    // Track API calls
    const trackAPI = useCallback((
        endpoint: string, 
        method: string, 
        status: number, 
        responseTime?: number
    ) => {
        if (isFeatureEnabled('TRACK_API_CALLS')) {
            trackAPICall(endpoint, method, status, responseTime);
        }
    }, []);

    // Track errors
    const trackErrorEvent = useCallback((
        errorMessage: string, 
        errorStack?: string, 
        errorContext?: Record<string, any>
    ) => {
        if (isFeatureEnabled('TRACK_ERRORS')) {
            trackError(errorMessage, errorStack, errorContext);
        }
    }, []);

    // Track performance metrics
    const trackPerformanceMetric = useCallback((
        metricName: string, 
        value: number, 
        rating?: string
    ) => {
        if (isFeatureEnabled('TRACK_PERFORMANCE')) {
            trackPerformance(metricName, value, rating);
        }
    }, []);

    // Custom event tracking
    const trackCustomEvent = useCallback((
        eventName: string, 
        eventData: Record<string, any> = {}
    ) => {
        if (isAnalyticsEnabled()) {
            sendGTMEvent(eventName, eventData);
        }
    }, []);

    return {
    // Status
        isEnabled: isAnalyticsEnabled(),
        isAvailable: isGTMAvailable(),
    
        // Tracking functions
        trackClick,
        trackForm,
        trackFile,
        trackAPI,
        trackError: trackErrorEvent,
        trackPerformance: trackPerformanceMetric,
        trackCustomEvent,
    
        // Direct access
        sendGTMEvent,
        trackPageView,
        trackUserInteraction,
        trackFormSubmission,
        trackFileUpload,
        trackAPICall,
    
        // Configuration
        config: ANALYTICS_CONFIG,
    };
}; 